import React from "react";
import axios from "axios";
import * as Icon from "react-feather";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  TextField,
} from "@mui/material";
import { useAppRegistryStore } from "../../../stores/appRegistryStore";
import "./Agreements.css";

const STATUS_OPTIONS = ["all", "Pending", "Paid", "Overdue", "Cancelled"];
const STATUS_TRANSLATION_KEYS = {
  Pending: "agreements.status.pending",
  Paid: "agreements.status.paid",
  Overdue: "agreements.status.overdue",
  Cancelled: "agreements.status.cancelled",
};
const DEFAULT_DEPOSIT_PERCENTAGE = Number(
  process.env.REACT_APP_DEFAULT_DEPOSIT_PERCENTAGE || 50,
);
const NEW_AGREEMENT_INITIAL_FORM = {
  schoolName: "",
  schoolRFC: "",
  contactEmail: "",
  licenseCount: 1,
  billingCycle: "monthly",
  planName: "",
  totalAmount: "",
  depositPercentage: DEFAULT_DEPOSIT_PERCENTAGE,
};

// Precio por licencia según el tramo de volumen, igual que en la landing page
const getPlanTierPrice = (plan, licenses, rangeLimit) => {
  if (!plan || !rangeLimit) return 0;

  for (let i = 1; i <= 8; i += 1) {
    const min = Number(rangeLimit[`min${i}`]);
    const max = Number(rangeLimit[`max${i}`]);
    if (
      Number.isFinite(min) &&
      Number.isFinite(max) &&
      licenses >= min &&
      licenses <= max
    ) {
      return Number(plan[`price${i}`]) || 0;
    }
  }

  return Number(plan.price1) || 0;
};

// Réplica exacta del cálculo de la landing page (dynamicPrice), en USD
const computeDefaultTotalUsd = ({
  plan,
  licenses,
  billingCycle,
  rangeLimit,
  annualSavePercent,
}) => {
  if (!plan || !rangeLimit || !Number.isFinite(licenses) || licenses <= 0) {
    return 0;
  }

  const min1 = Number(rangeLimit.min1);
  // Por debajo del primer tramo se cobra el mínimo de licencias
  const billableLicenses =
    Number.isFinite(min1) && licenses <= min1 ? min1 : licenses;
  const tierPrice = getPlanTierPrice(plan, licenses, rangeLimit);
  if (!tierPrice) return 0;

  if (billingCycle === "annual") {
    return (
      (billableLicenses * tierPrice * 12 * (100 - annualSavePercent)) / 100
    );
  }

  return billableLicenses * tierPrice;
};

const Agreements = () => {
  const history = useHistory();
  const { t, i18n } = useTranslation("common");
  const url = process.env.REACT_APP_BASEURL;
  const { settings } = useAppRegistryStore();
  const isDarkMode = settings.currentTheme === "dark";

  const [agreements, setAgreements] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [searchTerm, setSearchTerm] = React.useState("");
  const [confirmDialog, setConfirmDialog] = React.useState({
    open: false,
    agreement: null,
  });
  const [extendDialog, setExtendDialog] = React.useState({
    open: false,
    agreement: null,
  });
  const [authorizationCode, setAuthorizationCode] = React.useState("");
  const [newDueDate, setNewDueDate] = React.useState("");
  const [confirming, setConfirming] = React.useState(false);
  const [extending, setExtending] = React.useState(false);
  const [creating, setCreating] = React.useState(false);
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
  const [newAgreementForm, setNewAgreementForm] = React.useState(
    NEW_AGREEMENT_INITIAL_FORM,
  );
  // Catálogo de planes y tipo de cambio para sugerir el precio por defecto
  const [planOptions, setPlanOptions] = React.useState([]);
  const [rangeLimit, setRangeLimit] = React.useState(null);
  const [annualSavePercent, setAnnualSavePercent] = React.useState(0);
  const [usdToMxnRate, setUsdToMxnRate] = React.useState(null);
  const [pricingLoading, setPricingLoading] = React.useState(false);
  const [isTotalAmountEdited, setIsTotalAmountEdited] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: "",
    severity: "info",
  });

  const token = window.sessionStorage.getItem("Token");
  const user = window.sessionStorage.getItem("User");

  const getHeaders = () => ({
    "x-access-token": window.sessionStorage.getItem("Token"),
  });

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const loadAgreements = React.useCallback(
    async (status = "all", q = "") => {
      try {
        setLoading(true);
        const params = { limit: "all" };
        if (status && status !== "all") params.status = status;
        if (q && q.trim()) params.q = q.trim();

        const res = await axios.get(`${url}/api/admin/agreements`, {
          headers: getHeaders(),
          params,
        });

        setAgreements(res.data?.result || res.data?.rows || []);
      } catch (err) {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          sessionStorage.clear();
          history.push("/");
          return;
        }
        showSnackbar(
          err?.response?.data?.message || t("agreements.messages.loadError"),
          "error",
        );
      } finally {
        setLoading(false);
      }
    },
    [history, t, url],
  );

  React.useEffect(() => {
    if (!token || !user) {
      history.push("/");
      return;
    }
    loadAgreements(statusFilter, searchTerm);
  }, [history, loadAgreements, searchTerm, statusFilter, token, user]);

  React.useEffect(() => {
    if (!token) return;
    const timeoutId = window.setTimeout(() => {
      loadAgreements(statusFilter, searchTerm);
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [loadAgreements, searchTerm, statusFilter, token]);

  const formatMoney = (amount, currency = "MXN") => {
    const numericAmount = Number(amount || 0);
    return new Intl.NumberFormat(i18n.language === "es" ? "es-MX" : "en-US", {
      style: "currency",
      currency: currency || "MXN",
    }).format(numericAmount);
  };

  const formatDate = (value) => {
    if (!value) return t("agreements.common.na");
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return t("agreements.common.na");
    return date.toLocaleDateString(i18n.language === "es" ? "es-MX" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusClassName = (status) => {
    if (status === "Paid") return "agreements-badge agreements-badge-paid";
    if (status === "Overdue") {
      return "agreements-badge agreements-badge-overdue";
    }
    if (status === "Cancelled") {
      return "agreements-badge agreements-badge-cancelled";
    }
    return "agreements-badge agreements-badge-pending";
  };

  const openConfirmDialog = (agreement) => {
    setAuthorizationCode("");
    setConfirmDialog({ open: true, agreement });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, agreement: null });
    setAuthorizationCode("");
  };

  const openExtendDialog = (agreement) => {
    setNewDueDate(
      agreement?.DueDate
        ? new Date(agreement.DueDate).toISOString().slice(0, 10)
        : "",
    );
    setExtendDialog({ open: true, agreement });
  };

  const closeExtendDialog = () => {
    setExtendDialog({ open: false, agreement: null });
    setNewDueDate("");
  };

  const openCreateDialog = () => {
    setNewAgreementForm(NEW_AGREEMENT_INITIAL_FORM);
    setIsTotalAmountEdited(false);
    setCreateDialogOpen(true);
  };

  const closeCreateDialog = () => {
    if (creating) return;
    setCreateDialogOpen(false);
    setNewAgreementForm(NEW_AGREEMENT_INITIAL_FORM);
    setIsTotalAmountEdited(false);
  };

  const handleNewAgreementFieldChange = (field, value) => {
    if (field === "totalAmount") {
      setIsTotalAmountEdited(true);
    }
    setNewAgreementForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Catálogo de planes + tipo de cambio: el precio sugerido debe coincidir con
  // el que la escuela vería en la landing page (USD de lista x tipo de cambio)
  React.useEffect(() => {
    if (!createDialogOpen) return undefined;

    let isActive = true;
    setPricingLoading(true);

    // Los acuerdos de depósito son solo para México/MXN, así que el catálogo
    // debe venir de la cuenta de México: ahí no se vende Platinum. Usar el
    // catálogo general mostraría planes que la escuela no puede contratar.
    Promise.all([
      axios.get(`${url}/api/payment/get-mexico-price-config`),
      axios.get(`${url}/api/payment/get-exchange-rate?from=USD&to=MXN`),
    ])
      .then(([priceRes, rateRes]) => {
        if (!isActive) return;

        const plans = (priceRes.data?.prices || [])
          .map((price) => price?.acvPlan)
          .filter((plan) => plan && plan.planName)
          .sort((a, b) => Number(a.CardId || 0) - Number(b.CardId || 0));

        setPlanOptions(plans);
        setRangeLimit(priceRes.data?.rangeLimit || null);
        setAnnualSavePercent(Number(priceRes.data?.discount?.year1) || 0);

        const rate = Number(rateRes.data?.rate);
        setUsdToMxnRate(Number.isFinite(rate) && rate > 0 ? rate : null);
      })
      .catch(() => {
        if (!isActive) return;
        setPlanOptions([]);
        setRangeLimit(null);
        setUsdToMxnRate(null);
      })
      .finally(() => {
        if (isActive) setPricingLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [createDialogOpen, url]);

  const suggestedTotalAmount = React.useMemo(() => {
    const plan = planOptions.find(
      (item) => item.planName === newAgreementForm.planName,
    );
    if (!plan || !usdToMxnRate) return 0;

    const usdTotal = computeDefaultTotalUsd({
      plan,
      licenses: Number(newAgreementForm.licenseCount),
      billingCycle: newAgreementForm.billingCycle,
      rangeLimit,
      annualSavePercent,
    });

    return Number((usdTotal * usdToMxnRate).toFixed(2));
  }, [
    planOptions,
    rangeLimit,
    annualSavePercent,
    usdToMxnRate,
    newAgreementForm.planName,
    newAgreementForm.licenseCount,
    newAgreementForm.billingCycle,
  ]);

  // Prellenar el total mientras el admin no lo haya editado manualmente
  React.useEffect(() => {
    if (!createDialogOpen || isTotalAmountEdited) return;
    if (!suggestedTotalAmount) return;

    setNewAgreementForm((prev) =>
      Number(prev.totalAmount) === suggestedTotalAmount
        ? prev
        : { ...prev, totalAmount: String(suggestedTotalAmount) },
    );
  }, [createDialogOpen, isTotalAmountEdited, suggestedTotalAmount]);

  const parsedTotalAmount = Number(newAgreementForm.totalAmount || 0);
  const parsedDepositPercentage = Number(newAgreementForm.depositPercentage || 0);
  const previewDepositAmount =
    parsedTotalAmount > 0 && parsedDepositPercentage > 0
      ? (parsedTotalAmount * parsedDepositPercentage) / 100
      : 0;
  const previewBalanceAmount =
    parsedTotalAmount > 0 ? parsedTotalAmount - previewDepositAmount : 0;

  const handleConfirmAgreement = async () => {
    const agreementId = confirmDialog.agreement?.Id;
    if (!agreementId || !authorizationCode.trim()) {
      showSnackbar(t("agreements.messages.authorizationRequired"), "warning");
      return;
    }

    try {
      setConfirming(true);
      await axios.put(
        `${url}/api/admin/agreements/${agreementId}/confirm`,
        {
          authorizationCode: authorizationCode.trim(),
          confirmedBy: user,
        },
        { headers: getHeaders() },
      );

      closeConfirmDialog();
      showSnackbar(t("agreements.messages.confirmSuccess"), "success");
      loadAgreements(statusFilter, searchTerm);
    } catch (err) {
      showSnackbar(
        err?.response?.data?.message || t("agreements.messages.confirmError"),
        "error",
      );
    } finally {
      setConfirming(false);
    }
  };

  const handleExtendAgreement = async () => {
    const agreementId = extendDialog.agreement?.Id;
    if (!agreementId || !newDueDate) {
      showSnackbar(t("agreements.messages.extendRequired"), "warning");
      return;
    }

    try {
      setExtending(true);
      await axios.put(
        `${url}/api/admin/agreements/${agreementId}/extend`,
        { newDueDate },
        { headers: getHeaders() },
      );

      closeExtendDialog();
      showSnackbar(t("agreements.messages.extendSuccess"), "success");
      loadAgreements(statusFilter, searchTerm);
    } catch (err) {
      showSnackbar(
        err?.response?.data?.message || t("agreements.messages.extendError"),
        "error",
      );
    } finally {
      setExtending(false);
    }
  };

  const handleCreateAgreement = async () => {
    const payload = {
      schoolName: String(newAgreementForm.schoolName || "").trim(),
      schoolRFC: String(newAgreementForm.schoolRFC || "").trim(),
      contactEmail: String(newAgreementForm.contactEmail || "").trim(),
      licenseCount: Number(newAgreementForm.licenseCount || 0),
      billingCycle: String(newAgreementForm.billingCycle || "").trim().toLowerCase(),
      planName: String(newAgreementForm.planName || "").trim(),
      totalAmount: Number(newAgreementForm.totalAmount || 0),
      depositPercentage: Number(newAgreementForm.depositPercentage || 0),
    };

    if (
      !payload.schoolName ||
      !payload.contactEmail ||
      !payload.planName ||
      payload.licenseCount <= 0 ||
      !payload.totalAmount ||
      payload.totalAmount <= 0
    ) {
      showSnackbar(t("agreements.messages.createRequired"), "warning");
      return;
    }

    try {
      setCreating(true);
      await axios.post(`${url}/api/admin/agreements`, payload, {
        headers: getHeaders(),
      });
      setCreateDialogOpen(false);
      setNewAgreementForm(NEW_AGREEMENT_INITIAL_FORM);
      showSnackbar(t("agreements.messages.createSuccess"), "success");
      loadAgreements(statusFilter, searchTerm);
    } catch (err) {
      showSnackbar(
        err?.response?.data?.message || t("agreements.messages.createError"),
        "error",
      );
    } finally {
      setCreating(false);
    }
  };

  const getNextAllowedDueDate = (agreement) => {
    const currentDueDate = agreement?.DueDate
      ? new Date(agreement.DueDate)
      : null;
    if (!currentDueDate || Number.isNaN(currentDueDate.getTime())) return "";

    currentDueDate.setDate(currentDueDate.getDate() + 1);
    return currentDueDate.toISOString().slice(0, 10);
  };

  const summary = React.useMemo(() => {
    return agreements.reduce(
      (acc, agreement) => {
        const status = agreement?.Status || "Pending";
        acc.total += 1;
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      },
      {
        total: 0,
        Pending: 0,
        Paid: 0,
        Overdue: 0,
        Cancelled: 0,
      },
    );
  }, [agreements]);

  const translateStatus = (status) =>
    t(STATUS_TRANSLATION_KEYS[status] || STATUS_TRANSLATION_KEYS.Pending);

  return (
    <>
      <div className="mx-4 mt-58 agreements-page">
        <div className="d-flex justify-content-between align-items-center py-2 agreements-header">
          <div className="agreements-header-copy">
            <h5 className="section-title mb-0">
              <Icon.Briefcase size={32} className="mr-2 text-primary" />
              {t("agreements.pageTitle")}
            </h5>
            <p className="text-muted mb-0 agreements-page-subtitle">
              {t("agreements.pageSubtitle")}
            </p>
          </div>
          <div className="d-flex align-items-center">
            <button
              className="btn btn-primary mr-2"
              onClick={openCreateDialog}
              disabled={loading || creating}
            >
              <Icon.Plus size={16} className="mr-2" />
              {t("agreements.actions.newAgreement")}
            </button>
            <button
              className="btn btn-outline-primary agreements-refresh-button"
              onClick={() => loadAgreements(statusFilter, searchTerm)}
              disabled={loading}
            >
              <Icon.RefreshCw
                size={16}
                className={`mr-2 ${loading ? "spin" : ""}`}
              />
              {t("agreements.actions.refresh")}
            </button>
          </div>
        </div>

        <div className="row agreements-summary-row">
          <div className="col-md-6 col-xl-3 mb-3">
            <div className="agreements-summary-card">
              <span className="agreements-summary-label">
                {t("agreements.summary.total")}
              </span>
              <strong>{summary.total}</strong>
            </div>
          </div>
          <div className="col-md-6 col-xl-3 mb-3">
            <div className="agreements-summary-card">
              <span className="agreements-summary-label">
                {t("agreements.summary.pending")}
              </span>
              <strong>{summary.Pending}</strong>
            </div>
          </div>
          <div className="col-md-6 col-xl-3 mb-3">
            <div className="agreements-summary-card">
              <span className="agreements-summary-label">
                {t("agreements.summary.paid")}
              </span>
              <strong>{summary.Paid}</strong>
            </div>
          </div>
          <div className="col-md-6 col-xl-3 mb-3">
            <div className="agreements-summary-card">
              <span className="agreements-summary-label">
                {t("agreements.summary.overdue")}
              </span>
              <strong>{summary.Overdue}</strong>
            </div>
          </div>
        </div>

        <div className="card agreements-card mb-4">
          <div className="card-body">
            <div className="row align-items-end">
              <div className="col-md-4 mb-3">
                <label className="agreements-label" htmlFor="agreement-status">
                  {t("agreements.filters.status")}
                </label>
                <select
                  id="agreement-status"
                  className="form-control"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  {STATUS_OPTIONS.map((status) => (
                    <option key={status} value={status}>
                      {status === "all"
                        ? t("agreements.filters.all")
                        : translateStatus(status)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-8 mb-3">
                <label className="agreements-label" htmlFor="agreement-search">
                  {t("agreements.filters.search")}
                </label>
                <div className="agreements-search">
                  <span className="agreements-search-icon" aria-hidden="true">
                    <Icon.Search size={16} />
                  </span>
                  <input
                    id="agreement-search"
                    type="text"
                    className="form-control agreements-search-input"
                    placeholder={t("agreements.filters.searchPlaceholder")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card agreements-card">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table agreements-table mb-0">
                <thead>
                  <tr>
                    <th>{t("agreements.table.code")}</th>
                    <th>{t("agreements.table.school")}</th>
                    <th>{t("agreements.table.total")}</th>
                    <th>{t("agreements.table.deposit")}</th>
                    <th>{t("agreements.table.balance")}</th>
                    <th>{t("agreements.table.dueDate")}</th>
                    <th>{t("agreements.table.status")}</th>
                    <th className="agreements-actions-header">
                      {t("agreements.table.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {!loading && agreements.length === 0 && (
                    <tr>
                      <td colSpan="8" className="text-center py-5 text-muted">
                        <Icon.Inbox size={42} className="mb-3" />
                        <div>{t("agreements.empty")}</div>
                      </td>
                    </tr>
                  )}
                  {loading && (
                    <tr>
                      <td colSpan="8" className="text-center py-5 text-muted">
                        <Icon.RefreshCw size={24} className="mb-3 spin" />
                        <div>{t("agreements.loading")}</div>
                      </td>
                    </tr>
                  )}
                  {agreements.map((agreement) => {
                    const isRedeemedAgreement =
                      agreement.IsRedeemed === null ||
                      typeof agreement.IsRedeemed === "undefined"
                        ? true
                        : Boolean(Number(agreement.IsRedeemed || 0));
                    const canMutate =
                      (agreement.Status === "Pending" ||
                        agreement.Status === "Overdue") &&
                      Boolean(agreement.TenantId) &&
                      isRedeemedAgreement;

                    return (
                      <tr key={agreement.Id}>
                        <td>
                          <strong>
                            {agreement.AgreementCode || t("agreements.common.na")}
                          </strong>
                        </td>
                        <td>
                          <div className="agreements-school">
                            <strong>
                              {agreement.School || t("agreements.common.na")}
                            </strong>
                            <span>
                              {agreement.ContactEmail ||
                                t("agreements.common.na")}
                            </span>
                            <small>
                              {t("agreements.table.idLabel", {
                                id: agreement.Id,
                              })}
                            </small>
                          </div>
                        </td>
                        <td>
                          {formatMoney(
                            agreement.TotalAmount,
                            agreement.Currency,
                          )}
                        </td>
                        <td>
                          {formatMoney(
                            agreement.DepositAmount,
                            agreement.Currency,
                          )}
                        </td>
                        <td>
                          {formatMoney(
                            agreement.BalanceAmount,
                            agreement.Currency,
                          )}
                        </td>
                        <td>
                          <div className="agreements-date">
                            <span>{formatDate(agreement.DueDate)}</span>
                            <small>
                              {t("agreements.table.depositDateLabel", {
                                date: formatDate(agreement.DepositDate),
                              })}
                            </small>
                          </div>
                        </td>
                        <td>
                          <span
                            className={getStatusClassName(agreement.Status)}
                          >
                            {translateStatus(agreement.Status || "Pending")}
                          </span>
                        </td>
                        <td>
                          <div className="agreements-actions">
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => openConfirmDialog(agreement)}
                              disabled={!canMutate || confirming || extending}
                            >
                              <Icon.CheckCircle size={14} className="mr-1" />
                              {t("agreements.actions.confirm")}
                            </button>
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => openExtendDialog(agreement)}
                              disabled={!canMutate || confirming || extending}
                            >
                              <Icon.Calendar size={14} className="mr-1" />
                              {t("agreements.actions.extend")}
                            </button>
                          </div>
                          {!isRedeemedAgreement && (
                            <small className="text-muted d-block mt-1">
                              {t("agreements.notRedeemedHint")}
                            </small>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        open={createDialogOpen}
        onClose={closeCreateDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? "#1e1e1e" : "#ffffff",
            color: isDarkMode ? "#f5f5f5" : "#1a1a1a",
          },
        }}
      >
        <DialogTitle>{t("agreements.dialogs.create.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{ color: isDarkMode ? "#e0e0e0" : "#4b5563", mb: 2 }}
          >
            {t("agreements.dialogs.create.description")}
          </DialogContentText>
          <div className="row">
            <div className="col-md-6">
              <TextField
                label={t("agreements.form.schoolName")}
                value={newAgreementForm.schoolName}
                onChange={(e) =>
                  handleNewAgreementFieldChange("schoolName", e.target.value)
                }
                fullWidth
                required
                margin="dense"
              />
            </div>
            <div className="col-md-6">
              <TextField
                label={t("agreements.form.schoolRFC")}
                value={newAgreementForm.schoolRFC}
                onChange={(e) =>
                  handleNewAgreementFieldChange("schoolRFC", e.target.value)
                }
                fullWidth
                margin="dense"
              />
            </div>
            <div className="col-md-6">
              <TextField
                label={t("agreements.form.contactEmail")}
                type="email"
                value={newAgreementForm.contactEmail}
                onChange={(e) =>
                  handleNewAgreementFieldChange("contactEmail", e.target.value)
                }
                fullWidth
                required
                margin="dense"
              />
            </div>
            <div className="col-md-6">
              <TextField
                label={t("agreements.form.licenseCount")}
                type="number"
                value={newAgreementForm.licenseCount}
                onChange={(e) =>
                  handleNewAgreementFieldChange("licenseCount", e.target.value)
                }
                fullWidth
                required
                margin="dense"
                inputProps={{ min: 1 }}
              />
            </div>
            <div className="col-md-6">
              <label className="agreements-label mt-2" htmlFor="billing-cycle">
                {t("agreements.form.billingCycle")}
              </label>
              <select
                id="billing-cycle"
                className="form-control"
                value={newAgreementForm.billingCycle}
                onChange={(e) =>
                  handleNewAgreementFieldChange("billingCycle", e.target.value)
                }
              >
                <option value="monthly">
                  {t("agreements.form.billingCycleMonthly")}
                </option>
                <option value="annual">
                  {t("agreements.form.billingCycleAnnual")}
                </option>
              </select>
            </div>
            <div className="col-md-6">
              <label className="agreements-label mt-2" htmlFor="agreement-plan">
                {t("agreements.form.planName")}
              </label>
              <select
                id="agreement-plan"
                className="form-control"
                value={newAgreementForm.planName}
                onChange={(e) =>
                  handleNewAgreementFieldChange("planName", e.target.value)
                }
                disabled={pricingLoading}
              >
                <option value="">
                  {pricingLoading
                    ? t("agreements.form.planLoading")
                    : t("agreements.form.planPlaceholder")}
                </option>
                {planOptions.map((plan) => (
                  <option key={plan.planName} value={plan.planName}>
                    {plan.planName}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <TextField
                label={t("agreements.form.totalAmount")}
                type="number"
                value={newAgreementForm.totalAmount}
                onChange={(e) =>
                  handleNewAgreementFieldChange("totalAmount", e.target.value)
                }
                fullWidth
                required
                margin="dense"
                inputProps={{ min: 0.01, step: "0.01" }}
              />
              {suggestedTotalAmount > 0 && (
                <div className="small text-muted mt-1">
                  {t("agreements.form.defaultPrice")}{" "}
                  <strong>{formatMoney(suggestedTotalAmount)}</strong>
                  {isTotalAmountEdited &&
                    Number(newAgreementForm.totalAmount) !==
                      suggestedTotalAmount && (
                      <Button
                        size="small"
                        onClick={() => {
                          setNewAgreementForm((prev) => ({
                            ...prev,
                            totalAmount: String(suggestedTotalAmount),
                          }));
                          setIsTotalAmountEdited(false);
                        }}
                      >
                        {t("agreements.form.useDefaultPrice")}
                      </Button>
                    )}
                </div>
              )}
              {!pricingLoading &&
                newAgreementForm.planName &&
                !suggestedTotalAmount && (
                  <div className="small text-muted mt-1">
                    {t("agreements.form.defaultPriceUnavailable")}
                  </div>
                )}
            </div>
            <div className="col-md-6">
              <TextField
                label={t("agreements.form.depositPercentage")}
                type="number"
                value={newAgreementForm.depositPercentage}
                onChange={(e) =>
                  handleNewAgreementFieldChange("depositPercentage", e.target.value)
                }
                fullWidth
                required
                margin="dense"
                inputProps={{ min: 1, max: 99, step: "0.01" }}
              />
            </div>
            <div className="col-md-6 d-flex align-items-end">
              <div className="w-100">
                <div className="small text-muted">
                  {t("agreements.form.previewDeposit")}{" "}
                  <strong>{formatMoney(previewDepositAmount)}</strong>
                </div>
                <div className="small text-muted">
                  {t("agreements.form.previewBalance")}{" "}
                  <strong>{formatMoney(previewBalanceAmount)}</strong>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateDialog} disabled={creating}>
            {t("agreements.actions.cancel")}
          </Button>
          <Button
            onClick={handleCreateAgreement}
            variant="contained"
            color="primary"
            disabled={creating}
          >
            {creating
              ? t("agreements.actions.creating")
              : t("agreements.actions.createAgreement")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? "#1e1e1e" : "#ffffff",
            color: isDarkMode ? "#f5f5f5" : "#1a1a1a",
          },
        }}
      >
        <DialogTitle>{t("agreements.dialogs.confirm.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{ color: isDarkMode ? "#e0e0e0" : "#4b5563", mb: 2 }}
          >
            {t("agreements.dialogs.confirm.description")}
          </DialogContentText>
          <TextField
            label={t("agreements.dialogs.confirm.authorizationCode")}
            value={authorizationCode}
            onChange={(e) => setAuthorizationCode(e.target.value)}
            fullWidth
            required
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog} disabled={confirming}>
            {t("agreements.actions.cancel")}
          </Button>
          <Button
            onClick={handleConfirmAgreement}
            variant="contained"
            color="success"
            disabled={confirming || !authorizationCode.trim()}
          >
            {confirming
              ? t("agreements.actions.confirming")
              : t("agreements.actions.confirmBalance")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={extendDialog.open}
        onClose={closeExtendDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? "#1e1e1e" : "#ffffff",
            color: isDarkMode ? "#f5f5f5" : "#1a1a1a",
          },
        }}
      >
        <DialogTitle>{t("agreements.dialogs.extend.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{ color: isDarkMode ? "#e0e0e0" : "#4b5563", mb: 2 }}
          >
            {t("agreements.dialogs.extend.description")}
          </DialogContentText>
          <TextField
            type="date"
            label={t("agreements.dialogs.extend.newDate")}
            value={newDueDate}
            onChange={(e) => setNewDueDate(e.target.value)}
            fullWidth
            required
            margin="dense"
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: getNextAllowedDueDate(extendDialog.agreement),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeExtendDialog} disabled={extending}>
            {t("agreements.actions.cancel")}
          </Button>
          <Button
            onClick={handleExtendAgreement}
            variant="contained"
            color="primary"
            disabled={extending || !newDueDate}
          >
            {extending
              ? t("agreements.actions.saving")
              : t("agreements.actions.saveDate")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Agreements;
